// Today's plan before it starts (walkthrough 4: "after a reload the edited
// plan is still there"): the engine's answer, the request it came from (so
// Start and "Other options" send the same check-in and minutes) and the
// person's edits, kept at `mono.training.<userId>.draft`. An exercise brought
// in whose numbers the engine hadn't sent yet keeps its `asking` mark in the
// plan (planEdit.markAsking), so the plan view asks again after a reload.
//
// A draft never outlives a session: it is cleared when one starts — and
// whenever Today finds a session open — because a plan worked out before a
// session is stale after it. It also goes stale with the day: readiness and
// this week's sets move by the hour, so one planned more than
// DRAFT_MAX_AGE_MS ago is dropped on read — and not started (isFreshDraft).
// Nor does it outlive the sign-in: logout clears every draft (clearAllDrafts).
import { readStored, storedKeys, trainingKey, writeStored } from "./storage";

export const DRAFT_VERSION = 1;
// Ours: "the same training day". The engine reads recovery in hours (τ 30 h),
// so a morning plan is still fair in the evening; yesterday's isn't.
export const DRAFT_MAX_AGE_MS = 12 * 60 * 60 * 1000;

export const draftKey = (userId) => trainingKey(userId, "draft");

const hasUser = (userId) => userId !== null && userId !== undefined && userId !== "";
const isObject = (value) => value !== null && typeof value === "object" && !Array.isArray(value);

/** A plan the server could start: a template and items naming exercises. */
export function isPlan(plan) {
  return (
    isObject(plan) &&
    typeof plan.template === "string" &&
    Array.isArray(plan.items) &&
    plan.items.every(
      (item) => isObject(item) && typeof item.exercise === "string" && Number.isInteger(item.sets),
    )
  );
}

/** A stored draft this version can use (junk and other versions are not). */
export function isDraft(value) {
  return (
    isObject(value) &&
    value.v === DRAFT_VERSION &&
    typeof value.plannedAt === "number" &&
    Number.isFinite(value.plannedAt) &&
    isObject(value.request) &&
    isObject(value.next) &&
    isPlan(value.next.plan) &&
    isPlan(value.plan) &&
    typeof value.edited === "boolean"
  );
}

/**
 * A fresh draft from a next/ answer.
 * - `request`: what next/ was asked — `{checkin, minutes, template}`
 *   (each null when not given).
 * - `next`: the answer (TrainingNext) — reasons, notices, other templates.
 * - `plan`: what Start sends (next.plan until the person edits it).
 */
export function makeDraft({ request, next, plan = next?.plan, edited = false }, now = Date.now()) {
  return {
    v: DRAFT_VERSION,
    plannedAt: now,
    request: {
      checkin: request?.checkin ?? null,
      minutes: request?.minutes ?? null,
      template: request?.template ?? null,
    },
    next,
    plan,
    edited,
  };
}

/**
 * Still today's: planned at most DRAFT_MAX_AGE_MS ago. Asked on read, and
 * again at Start and when the tab comes back into view — a plan left on
 * screen overnight is yesterday's too (s7).
 */
export function isFreshDraft(draft, now = Date.now()) {
  const age = isDraft(draft) ? now - draft.plannedAt : Infinity;
  // a clock moved back puts a draft in the future: not today's either
  return age >= 0 && age <= DRAFT_MAX_AGE_MS;
}

/**
 * The person's draft, or null: none stored, no person yet, junk, another
 * version, or planned too long ago (a stale or unusable one is removed).
 */
export function readDraft(userId, now = Date.now()) {
  if (!hasUser(userId)) return null;
  const key = draftKey(userId);
  const value = readStored(key);
  if (value === undefined || value === null) return null;
  if (!isFreshDraft(value, now)) {
    writeStored(key, null);
    return null;
  }
  return value;
}

/**
 * Keep `draft` for this person. Returns true when it reached localStorage;
 * false when it is only held for this visit (blocked storage — say so) or
 * there is no person to keep it for.
 */
export function saveDraft(userId, draft) {
  if (!hasUser(userId)) return false;
  return writeStored(draftKey(userId), draft);
}

/** Forget this person's draft (a session started, or one is open). */
export function clearDraft(userId) {
  if (!hasUser(userId)) return;
  writeStored(draftKey(userId), null);
}

// A draft's key, for any person: `mono.training.<id>.draft` and nothing
// deeper — a set queue lives at `mono.training.<id>.queue.<sessionId>`.
const ANY_DRAFT_KEY = /^mono\.training\.[^.]+\.draft$/;

/**
 * Forget every draft on this device, whoever planned it. Sign-out calls
 * this (AuthContext.logout, c6): a draft holds the check-in — sleep,
 * stress, any pain and where — which shouldn't stay on a shared phone
 * once its owner has signed out. Every person's, not only the one signing
 * out: one left by an earlier sign-in goes too, and sign-out needn't know
 * the id the key carries (it's /users/me/'s).
 *
 * Drafts only. The set queue (setQueue.js) is deliberately left alone: it
 * holds logged sets the server hasn't acknowledged, which must survive
 * until they are sent (D14). A 401 signs the person out in the middle of a
 * session (services/api.js), and the sets queued before it go through when
 * they sign back in; it is keyed per person, so it never reaches another
 * account. The rest timer stays too: it says nothing about the person.
 */
export function clearAllDrafts() {
  for (const key of storedKeys("mono.training.")) {
    if (ANY_DRAFT_KEY.test(key)) writeStored(key, null);
  }
}
